import React, { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import './QRCodeGenerator.css';
import usePageMeta from '../../hooks/usePageMeta';

type QRType = 'url' | 'text' | 'wifi' | 'vcard' | 'email' | 'sms' | 'whatsapp';

interface DesignOptions {
  foregroundColor: string;
  backgroundColor: string;
  size: number;
}

interface WiFiData {
  ssid: string;
  password: string;
  encryption: 'WPA' | 'WEP' | 'nopass';
  hidden: boolean;
}

interface VCardData {
  name: string;
  phone: string;
  email: string;
  company: string;
  website: string;
}

interface EmailData {
  to: string;
  subject: string;
  body: string;
}

interface SMSData {
  phone: string;
  message: string;
}

const QRCodeGenerator: React.FC = () => {
  usePageMeta({
    title: 'Gerador de QR Code Grátis',
    description:
      'Gere QR Codes gratuitos para links, Wi-Fi, vCard, e-mail, SMS e WhatsApp. Personalize as cores e baixe em alta resolução.',
    path: '/free-tools/qr-code-generator',
  });

  const [qrType, setQrType] = useState<QRType>('url');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [designOptions, setDesignOptions] = useState<DesignOptions>({
    foregroundColor: '#000000',
    backgroundColor: '#FFFFFF',
    size: 300,
  });

  // States para diferentes tipos
  const [urlData, setUrlData] = useState('');
  const [textData, setTextData] = useState('');
  const [wifiData, setWiFiData] = useState<WiFiData>({
    ssid: '',
    password: '',
    encryption: 'WPA',
    hidden: false,
  });
  const [vcardData, setVCardData] = useState<VCardData>({
    name: '',
    phone: '',
    email: '',
    company: '',
    website: '',
  });
  const [emailData, setEmailData] = useState<EmailData>({
    to: '',
    subject: '',
    body: '',
  });
  const [smsData, setSMSData] = useState<SMSData>({
    phone: '',
    message: '',
  });
  const [whatsappData, setWhatsappData] = useState<SMSData>({
    phone: '',
    message: '',
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Função para gerar o conteúdo do QR Code baseado no tipo
  const getQRContent = useCallback((): string => {
    switch (qrType) {
      case 'url':
        return urlData;
      
      case 'text':
        return textData;
      
      case 'wifi':
        if (!wifiData.ssid) return '';
        return `WIFI:T:${wifiData.encryption};S:${wifiData.ssid};P:${wifiData.password};H:${wifiData.hidden};`;
      
      case 'vcard':
        if (!vcardData.name) return '';
        return `BEGIN:VCARD\nVERSION:3.0\nFN:${vcardData.name}\nTEL:${vcardData.phone}\nEMAIL:${vcardData.email}\nORG:${vcardData.company}\nURL:${vcardData.website}\nEND:VCARD`;
      
      case 'email':
        if (!emailData.to) return '';
        return `mailto:${emailData.to}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.body)}`;
      
      case 'sms':
        if (!smsData.phone) return '';
        return `sms:${smsData.phone}?body=${encodeURIComponent(smsData.message)}`;
      
      case 'whatsapp':
        if (!whatsappData.phone) return '';
        const cleanPhone = whatsappData.phone.replace(/\D/g, '');
        return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(whatsappData.message)}`;
      
      default:
        return '';
    }
  }, [qrType, urlData, textData, wifiData, vcardData, emailData, smsData, whatsappData]);

  // Função para gerar o QR Code
  const generateQRCode = useCallback(async () => {
    const content = getQRContent();
    if (!content) {
      setQrCodeUrl('');
      return;
    }

    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      await QRCode.toCanvas(canvas, content, {
        width: designOptions.size,
        margin: 1,
        color: {
          dark: designOptions.foregroundColor,
          light: designOptions.backgroundColor,
        },
        errorCorrectionLevel: 'M',
      });

      setQrCodeUrl(canvas.toDataURL('image/png'));
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
    }
  }, [getQRContent, designOptions]);

  // Gerar QR Code automaticamente quando dados mudarem
  useEffect(() => {
    const content = getQRContent();
    if (content) {
      generateQRCode();
    } else {
      setQrCodeUrl('');
    }
  }, [getQRContent, generateQRCode]);

  // Handler para mudanças nas opções de design
  const handleDesignChange = (key: keyof DesignOptions, value: string | number) => {
    setDesignOptions(prev => ({ ...prev, [key]: value }));
  };

  // Download do QR Code
  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.download = `qrcode-${qrType}-${Date.now()}.png`;
    link.href = qrCodeUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Renderizar campos de input baseado no tipo
  const renderInputFields = () => {
    switch (qrType) {
      case 'url':
        return (
          <div className="input-group">
            <label htmlFor="url-input" className="input-label">
              🔗 URL
            </label>
            <input
              id="url-input"
              type="url"
              className="url-input"
              placeholder="https://exemplo.com"
              value={urlData}
              onChange={(e) => setUrlData(e.target.value)}
            />
          </div>
        );

      case 'text':
        return (
          <div className="input-group">
            <label htmlFor="text-input" className="input-label">
              📝 Texto
            </label>
            <textarea
              id="text-input"
              className="text-input"
              placeholder="Digite seu texto aqui..."
              value={textData}
              onChange={(e) => setTextData(e.target.value)}
            />
          </div>
        );

      case 'wifi':
        return (
          <>
            <div className="input-group">
              <label htmlFor="wifi-ssid" className="input-label">
                📶 Nome da Rede (SSID)
              </label>
              <input
                id="wifi-ssid"
                type="text"
                className="wifi-input"
                placeholder="MinhaRede"
                value={wifiData.ssid}
                onChange={(e) => setWiFiData({ ...wifiData, ssid: e.target.value })}
              />
            </div>
            <div className="input-grid">
              <div className="input-group">
                <label htmlFor="wifi-password" className="input-label">
                  🔒 Senha
                </label>
                <input
                  id="wifi-password"
                  type="text"
                  className="wifi-input"
                  placeholder="senha123"
                  value={wifiData.password}
                  onChange={(e) => setWiFiData({ ...wifiData, password: e.target.value })}
                />
              </div>
              <div className="input-group">
                <label htmlFor="wifi-encryption" className="input-label">
                  🛡️ Segurança
                </label>
                <select
                  id="wifi-encryption"
                  value={wifiData.encryption}
                  onChange={(e) => setWiFiData({ ...wifiData, encryption: e.target.value as 'WPA' | 'WEP' | 'nopass' })}
                >
                  <option value="WPA">WPA/WPA2</option>
                  <option value="WEP">WEP</option>
                  <option value="nopass">Sem Senha</option>
                </select>
              </div>
            </div>
          </>
        );

      case 'vcard':
        return (
          <>
            <div className="input-grid">
              <div className="input-group">
                <label htmlFor="vcard-name" className="input-label">
                  👤 Nome Completo
                </label>
                <input
                  id="vcard-name"
                  type="text"
                  className="vcard-input"
                  placeholder="João Silva"
                  value={vcardData.name}
                  onChange={(e) => setVCardData({ ...vcardData, name: e.target.value })}
                />
              </div>
              <div className="input-group">
                <label htmlFor="vcard-phone" className="input-label">
                  📱 Telefone
                </label>
                <input
                  id="vcard-phone"
                  type="tel"
                  className="vcard-input"
                  placeholder="+55 21 99999-9999"
                  value={vcardData.phone}
                  onChange={(e) => setVCardData({ ...vcardData, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="input-grid">
              <div className="input-group">
                <label htmlFor="vcard-email" className="input-label">
                  ✉️ Email
                </label>
                <input
                  id="vcard-email"
                  type="email"
                  className="vcard-input"
                  placeholder="joao@exemplo.com"
                  value={vcardData.email}
                  onChange={(e) => setVCardData({ ...vcardData, email: e.target.value })}
                />
              </div>
              <div className="input-group">
                <label htmlFor="vcard-company" className="input-label">
                  🏢 Empresa
                </label>
                <input
                  id="vcard-company"
                  type="text"
                  className="vcard-input"
                  placeholder="UpdateCommit"
                  value={vcardData.company}
                  onChange={(e) => setVCardData({ ...vcardData, company: e.target.value })}
                />
              </div>
            </div>
            <div className="input-group">
              <label htmlFor="vcard-website" className="input-label">
                🌐 Website
              </label>
              <input
                id="vcard-website"
                type="url"
                className="vcard-input"
                placeholder="https://updatecommit.com"
                value={vcardData.website}
                onChange={(e) => setVCardData({ ...vcardData, website: e.target.value })}
              />
            </div>
          </>
        );

      case 'email':
        return (
          <>
            <div className="input-group">
              <label htmlFor="email-to" className="input-label">
                📧 Destinatário
              </label>
              <input
                id="email-to"
                type="email"
                className="email-input"
                placeholder="destinatario@exemplo.com"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
              />
            </div>
            <div className="input-group">
              <label htmlFor="email-subject" className="input-label">
                📋 Assunto
              </label>
              <input
                id="email-subject"
                type="text"
                className="email-input"
                placeholder="Assunto do email"
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
              />
            </div>
            <div className="input-group">
              <label htmlFor="email-body" className="input-label">
                💬 Mensagem
              </label>
              <textarea
                id="email-body"
                className="text-input"
                placeholder="Corpo do email..."
                value={emailData.body}
                onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
              />
            </div>
          </>
        );

      case 'sms':
        return (
          <>
            <div className="input-group">
              <label htmlFor="sms-phone" className="input-label">
                📱 Número de Telefone
              </label>
              <input
                id="sms-phone"
                type="tel"
                className="sms-input"
                placeholder="+5521999999999"
                value={smsData.phone}
                onChange={(e) => setSMSData({ ...smsData, phone: e.target.value })}
              />
            </div>
            <div className="input-group">
              <label htmlFor="sms-message" className="input-label">
                💬 Mensagem
              </label>
              <textarea
                id="sms-message"
                className="text-input"
                placeholder="Digite sua mensagem..."
                value={smsData.message}
                onChange={(e) => setSMSData({ ...smsData, message: e.target.value })}
              />
            </div>
          </>
        );

      case 'whatsapp':
        return (
          <>
            <div className="input-group">
              <label htmlFor="whatsapp-phone" className="input-label">
                📱 Número WhatsApp (com DDI)
              </label>
              <input
                id="whatsapp-phone"
                type="tel"
                className="sms-input"
                placeholder="5521999999999"
                value={whatsappData.phone}
                onChange={(e) => setWhatsappData({ ...whatsappData, phone: e.target.value })}
              />
            </div>
            <div className="input-group">
              <label htmlFor="whatsapp-message" className="input-label">
                💬 Mensagem Pré-preenchida
              </label>
              <textarea
                id="whatsapp-message"
                className="text-input"
                placeholder="Olá! Gostaria de mais informações..."
                value={whatsappData.message}
                onChange={(e) => setWhatsappData({ ...whatsappData, message: e.target.value })}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="qr-generator-container">
      <div className="qr-generator-card">
        <h1 className="qr-title">Gerador de QR Code</h1>
        <p className="qr-subtitle">Crie QR Codes personalizados para diferentes finalidades</p>

        <div className="qr-content">
          {/* Seletor de Tipo */}
          <div className="type-selector">
            <label className="type-label">📋 Tipo de QR Code</label>
            <div className="type-tabs">
              <button
                className={`type-tab ${qrType === 'url' ? 'active' : ''}`}
                onClick={() => setQrType('url')}
              >
                <span className="type-tab-icon">🔗</span>
                URL
              </button>
              <button
                className={`type-tab ${qrType === 'text' ? 'active' : ''}`}
                onClick={() => setQrType('text')}
              >
                <span className="type-tab-icon">📝</span>
                Texto
              </button>
              <button
                className={`type-tab ${qrType === 'wifi' ? 'active' : ''}`}
                onClick={() => setQrType('wifi')}
              >
                <span className="type-tab-icon">📶</span>
                WiFi
              </button>
              <button
                className={`type-tab ${qrType === 'vcard' ? 'active' : ''}`}
                onClick={() => setQrType('vcard')}
              >
                <span className="type-tab-icon">👤</span>
                Contato
              </button>
              <button
                className={`type-tab ${qrType === 'email' ? 'active' : ''}`}
                onClick={() => setQrType('email')}
              >
                <span className="type-tab-icon">📧</span>
                Email
              </button>
              <button
                className={`type-tab ${qrType === 'sms' ? 'active' : ''}`}
                onClick={() => setQrType('sms')}
              >
                <span className="type-tab-icon">💬</span>
                SMS
              </button>
              <button
                className={`type-tab ${qrType === 'whatsapp' ? 'active' : ''}`}
                onClick={() => setQrType('whatsapp')}
              >
                <span className="type-tab-icon">📲</span>
                WhatsApp
              </button>
            </div>
          </div>

          {/* Campos de Input Dinâmicos */}
          <div className="input-section">
            {renderInputFields()}
          </div>

          {/* Opções de Design */}
          <div className="design-section">
            <h3 className="section-title">🎨 Personalização</h3>
            
            <div className="color-grid">
              <div className="color-input-group">
                <label htmlFor="fg-color" className="color-label">
                  Cor Principal
                </label>
                <div className="color-input-wrapper">
                  <input
                    id="fg-color"
                    type="color"
                    className="color-input"
                    value={designOptions.foregroundColor}
                    onChange={(e) => handleDesignChange('foregroundColor', e.target.value)}
                  />
                  <span className="color-value">{designOptions.foregroundColor}</span>
                </div>
              </div>

              <div className="color-input-group">
                <label htmlFor="bg-color" className="color-label">
                  Cor de Fundo
                </label>
                <div className="color-input-wrapper">
                  <input
                    id="bg-color"
                    type="color"
                    className="color-input"
                    value={designOptions.backgroundColor}
                    onChange={(e) => handleDesignChange('backgroundColor', e.target.value)}
                  />
                  <span className="color-value">{designOptions.backgroundColor}</span>
                </div>
              </div>
            </div>

            <div className="size-control">
              <label htmlFor="size-input" className="size-label">
                📏 Tamanho: {designOptions.size}px
              </label>
              <input
                id="size-input"
                type="range"
                className="size-slider"
                min="200"
                max="600"
                step="50"
                value={designOptions.size}
                onChange={(e) => handleDesignChange('size', parseInt(e.target.value, 10))}
              />
            </div>
          </div>

          {/* Canvas oculto */}
          <canvas ref={canvasRef} style={{ display: 'none' }} aria-hidden="true" />

          {/* Preview do QR Code */}
          {qrCodeUrl ? (
            <div className="qr-preview">
              <h3 className="preview-title">✨ Seu QR Code</h3>
              <div className="qr-preview-wrapper">
                <img src={qrCodeUrl} alt="QR Code gerado" className="qr-image" />
              </div>
              <button onClick={downloadQRCode} className="download-btn" type="button">
                ⬇️ Baixar QR Code
              </button>
            </div>
          ) : (
            <div className="qr-placeholder">
              <div className="placeholder-icon">📱</div>
              <p>Preencha os campos acima para gerar seu QR Code</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;